/**
 * useAppPreferences — UI preferences (theme, fontSize, locale, sidebar, fullscreen)
 *
 * Persists:
 *   - `sidebarExpanded` → localStorage
 *   - `retailcliq_header_theme` → localStorage
 *   - `headerTheme` / `theme` → backend user-prefs (best effort)
 *
 * Also handles font-size CSS var and html/body `data-theme` attribute.
 */
import { useCallback, useEffect, useState } from 'react';

const FONT_SIZES = { small: '13px', medium: '14px', large: '16px' };

export default function useAppPreferences({ globalContextData }) {
  const [theme, setTheme] = useState('light');
  const [fontSize, setFontSize] = useState('medium');
  const [language, setLanguage] = useState('English');
  const [currency, setCurrency] = useState('USD');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebarExpanded');
    return saved !== null ? saved === 'true' : true;
  });
  const [headerTheme, setHeaderTheme] = useState(
    () => localStorage.getItem('retailcliq_header_theme') || 'default'
  );

  // Apply data-theme attribute on html/body
  useEffect(() => {
    const t = headerTheme === 'default' ? '' : headerTheme;
    document.documentElement.setAttribute('data-theme', t);
    document.body.setAttribute('data-theme', t);
  }, [headerTheme]);

  // Persist sidebar preference
  useEffect(() => {
    localStorage.setItem('sidebarExpanded', sidebarExpanded.toString());
  }, [sidebarExpanded]);

  // Apply font size
  useEffect(() => {
    document.documentElement.style.fontSize = FONT_SIZES[fontSize] || '14px';
  }, [fontSize]);

  // Sync user prefs from backend once user is known
  useEffect(() => {
    const uid = globalContextData?.userId || globalContextData?.UserId;
    if (!uid) return;
    let cancelled = false;
    import('@/services/api/userManagementService').then(({ userProfiles }) => {
      userProfiles.getPrefs(uid).then((res) => {
        if (cancelled) return;
        const prefs = res?.data?.data || {};
        if (prefs.headerTheme && prefs.headerTheme !== headerTheme) {
          setHeaderTheme(prefs.headerTheme);
          localStorage.setItem('retailcliq_header_theme', prefs.headerTheme);
          const dt = prefs.headerTheme === 'default' ? '' : prefs.headerTheme;
          document.documentElement.setAttribute('data-theme', dt);
          document.body.setAttribute('data-theme', dt);
        }
        if (prefs.theme && prefs.theme !== theme) setTheme(prefs.theme);
      }).catch(() => { /* silent */ });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalContextData?.userId, globalContextData?.UserId]);

  const handleHeaderThemeChange = useCallback((themeId) => {
    setHeaderTheme(themeId);
    localStorage.setItem('retailcliq_header_theme', themeId);
    const dt = themeId === 'default' ? '' : themeId;
    document.documentElement.setAttribute('data-theme', dt);
    document.body.setAttribute('data-theme', dt);
    const uid = globalContextData?.userId || globalContextData?.UserId;
    if (uid) {
      import('@/services/api/userManagementService').then(({ userProfiles }) => {
        userProfiles.setPref(uid, 'headerTheme', themeId).catch(() => { /* silent — local wins */ });
      });
    }
  }, [globalContextData]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => {
      const nt = t === 'dark' ? 'light' : 'dark';
      const uid = globalContextData?.userId || globalContextData?.UserId;
      if (uid) {
        import('@/services/api/userManagementService').then(({ userProfiles }) =>
          userProfiles.setPref(uid, 'theme', nt).catch(() => { /* silent */ })
        );
      }
      return nt;
    });
  }, [globalContextData]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => { /* blocked by browser — no-op */ });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Keep isFullscreen in sync with the REAL browser state — covers Escape key,
  // browser F11, or any other native exit path (not just our own toggle button).
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  return {
    theme, setTheme, toggleTheme,
    fontSize, setFontSize,
    language, setLanguage,
    currency, setCurrency,
    headerTheme, handleHeaderThemeChange,
    sidebarExpanded, setSidebarExpanded,
    isFullscreen, toggleFullscreen,
  };
}
